import React from "react";
import { useNavigate, useSearchParams } from "react-router";
import { useCallback, useState } from "react";
import {
    Toaster,
    Toast,
    ToastTitle,
    useToastController,
    useId,
} from "@fluentui/react-components";
import { privat_contact_form } from "../data/data";
import { buildInitialState, FormState } from "../utils/formUtils";
import NewPrivateContactForm from "../components/NewPrivateContactForm";
import { apiUrl } from "../utils/api";
import { buildPrivateContactCreatePayload } from "../utils/buildPrivateContactCreatePayload";
import type { ContactPageEmbedded } from "./NewCompanyContactPage";
import { privatVorbelegungAusParams } from "../utils/nameAusSuche";
import { nachAnlageZuweisen, ruecksprungAusParams } from "../utils/kundenakteRuecksprung";
import { mitZusatzPayload } from "../utils/refZuVorbelegung";

// Auto-vorbelegte Felder (Ownership-Preselect + Herkunft-Default) zählen NICHT
// als „dirty" — sonst wäre jedes frisch geöffnete Fenster sofort schmutzig.
const AUTO_POPULATED_KEYS = new Set([
    "foUserId", "boType", "boTeamId", "boUserId",
    "contactOriginSelections", "contactOriginOther",
    "citizenship", // Default „Deutsch" wird beim Öffnen gesetzt
    "country", // Land der Anschrift: Default „Deutschland" aus dem CountrySelect
]);

type NewPrivateContactPageProps = {
    embedded?: ContactPageEmbedded;
    /** Aus dem Einstieg „Neuer Kontakt" (Status & Rollen) — zählt nicht als dirty. */
    vorbelegung?: Record<string, string>;
    /** Wechsel auf die Firmenanlage; bekommt den aktuellen Formularstand. */
    onTypWechsel?: (felder: FormState) => void;
};

const NewPrivateContactPage: React.FC<NewPrivateContactPageProps> = ({ embedded, vorbelegung, onTypWechsel } = {}) => {
    const navigate = useNavigate();
    const toasterId = useId("new-private-contact-toaster");
    const { dispatchToast } = useToastController(toasterId);
    // „Kontakt nicht gefunden – neu anlegen" (globale Suche/Kontaktliste):
    // `?vorname=&nachname=` belegen die Namensfelder vor. Nur auf der Vollseite —
    // im Fenster-Modus gehört die URL der Seite HINTER dem Fenster. Nur beim
    // ersten Rendern gelesen.
    const [searchParams] = useSearchParams();
    const [urlVorbelegung] = useState<Record<string, string>>(() =>
        embedded ? {} : privatVorbelegungAusParams(searchParams),
    );
    const [ruecksprung] = useState(() => (embedded ? { zurueck: null, ordnerId: null } : ruecksprungAusParams(searchParams)));
    // Vorbelegung (SP8/A17, z. B. { emailPrivate } aus einem Termin-Teilnehmer,
    // oder der Suchbegriff) fließt auch in initialRef → zählt nicht als dirty.
    const [formFields, setFormFields] = useState<FormState>(() => ({
        ...buildInitialState(privat_contact_form),
        ...(embedded?.initialValues ?? {}),
        ...urlVorbelegung,
        ...(vorbelegung ?? {}),
    }));
    const initialRef = React.useRef<FormState>({
        ...buildInitialState(privat_contact_form),
        ...(embedded?.initialValues ?? {}),
        ...urlVorbelegung,
        ...(vorbelegung ?? {}),
    });
    const [saving, setSaving] = useState(false);

    const updateField = useCallback((name: string, value: string) => {
        setFormFields((prev) => ({ ...prev, [name]: value }));
    }, []);

    // Dirty-Tracking + Live-Titel (Nachname, Vorname) für den Fenster-Modus.
    React.useEffect(() => {
        if (embedded?.onDirtyChange) {
            const dirty = Object.keys(formFields).some(
                (k) => !AUTO_POPULATED_KEYS.has(k)
                    && (formFields[k] ?? "") !== (initialRef.current[k] ?? ""),
            );
            embedded.onDirtyChange(dirty);
        }
        if (embedded?.onTitleSuffix) {
            const last = String(formFields.lastname ?? "").trim();
            const first = String(formFields.firstname ?? "").trim();
            embedded.onTitleSuffix([last, first].filter(Boolean).join(", ") || null);
        }
    }, [formFields, embedded]);

    const notify = (intent: "success" | "error", message: string) =>
        dispatchToast(
            <Toast>
                <ToastTitle>{message}</ToastTitle>
            </Toast>,
            { intent },
        );

    const handleClose = () => {
        if (embedded?.onClose) { embedded.onClose(); return; }
        navigate("/contacts");
    };

    // Resolve the "Person" ContactType id (Contact.type is nullable, so a
    // missing id still creates a usable contact). Firma is id 1; Person varies.
    const resolvePersonTypeId = async (): Promise<number | undefined> => {
        try {
            const res = await fetch(apiUrl("contacttype"));
            const list = await res.json();
            const hit = Array.isArray(list)
                ? list.find((t: any) => String(t?.type).toLowerCase() === "person")
                : null;
            return typeof hit?.id === "number" ? hit.id : undefined;
        } catch {
            return undefined;
        }
    };

    const handleSave = async (): Promise<number | false> => {
        if (saving) return false;
        setSaving(true);
        try {
            const personTypeId = await resolvePersonTypeId();
            const payload = buildPrivateContactCreatePayload(formFields, personTypeId, {
                foUserId: (formFields.foUserId as string) || undefined,
                boType: (formFields.boType as "NONE" | "USER" | "TEAM") || undefined,
                boTeamId: (formFields.boTeamId as string) || undefined,
                boUserId: (formFields.boUserId as string) || undefined,
                inhouseContactUserId: (formFields.inhouseContactUserId as string) || undefined,
            });
            if (!payload) {
                notify("error", "Bitte mindestens einen Nachnamen angeben.");
                return false;
            }
            // Keine zweite Pflichtprüfung mehr: Pflicht sind ausschließlich
            // Nachname und Vorname (PO-Notiz 02.08., wie `privateRequiredChecks`).
            // Hier hing bis 09/2026 eine eigene Rufnummern-Sperre — sie galt nur
            // noch für Kunde/Sonstige und war damit nicht mehr synchron mit der
            // Regel in der Maske: Der Speichern-Knopf war frei, das Speichern
            // brach mit „Bitte Telefon oder Mobil angeben" ab. Genau das traf den
            // Normalfall, für den die Lockerung gedacht war — ein neuer
            // Mitarbeiter eines Kunden, dessen Durchwahl noch nicht vergeben ist
            // (Meldung Vertrieb 01.09.2026). Das Gegenstück bei der Firma
            // (`NewCompanyContactPage`) ist bereits seit 08/2026 ohne Sperre.
            const res = await fetch(apiUrl("contact"), {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(mitZusatzPayload(payload, embedded?.zusatzPayload)),
            });
            if (!res.ok) {
                notify("error", "Speichern fehlgeschlagen.");
                return false;
            }
            const data = await res.json().catch(() => null);
            const newId = data?.id ?? data?.contact?.id;
            if (typeof newId === "number") return newId; // Foto-Upload + Navigation via onSaveSuccess
            if (embedded?.onClose) { embedded.onClose(); return false; }
            navigate("/contacts");
            return false;
        } catch {
            notify("error", "Speichern fehlgeschlagen.");
            return false;
        } finally {
            setSaving(false);
        }
    };

    return (
        <>
            <Toaster toasterId={toasterId} />
            <NewPrivateContactForm
                formFields={formFields}
                updateField={updateField}
                onSave={handleSave}
                onSaveSuccess={(id) => {
                    if (embedded?.onSuccess) { embedded.onSuccess(id, formFields); return; }
                    // Aus dem Zuweisen-Popup der Alten Kundenakten („weitere Infos", B2).
                    if (ruecksprung.ordnerId) {
                        void nachAnlageZuweisen(ruecksprung.ordnerId, id).then(() =>
                            navigate(ruecksprung.zurueck ?? `/contacts/${id}`),
                        );
                        return;
                    }
                    navigate(`/contacts/${id}`);
                }}
                onClose={handleClose}
                speichernText={embedded?.speichernText}
                onTypWechsel={onTypWechsel ? () => onTypWechsel(formFields) : undefined}
            />
        </>
    );
};

export default NewPrivateContactPage;
